/**
 * The compiled message catalogue, one locale at a time.
 *
 * Server side every locale is preloaded (preloaded-locale-messages.ts), so `getLocaleMessages()`
 * always has what it needs. In the browser that module is stubbed out by the
 * `i18n-client-locale-split` Vite plugin, so each locale is its own lazily loaded chunk: client
 * entry points (pages/+onRenderClient.ts, web-static's +onBeforeRenderClient.ts) await
 * `ensureLocaleMessages(locale)` before rendering, after which synchronous reads work as before.
 *
 * Code that can run in the browser must read messages through here, never through `./index`
 * (which statically imports all three locales and would put them all back in the client bundle).
 */
import type { Messages } from '../types';
import { defaultLocale, supportedLocales, type AppLocale } from './supported-locales';
import { preloadedLocaleMessages } from './preloaded-locale-messages';

const loaded: Partial<Record<AppLocale, Messages>> = { ...preloadedLocaleMessages };
const pending: Partial<Record<AppLocale, Promise<Messages>>> = {};

// One literal import() per locale so the bundler emits exactly one chunk per catalogue.
const loaders: Record<AppLocale, () => Promise<{ default: Messages }>> = {
  en: () => import('./messages/en'),
  fi: () => import('./messages/fi'),
  sv: () => import('./messages/sv'),
};

const EMPTY: Messages = Object.freeze({}) as Messages;
const warnedMissing = new Set<string>();

export function toAppLocale(locale: string | null | undefined): AppLocale {
  return (supportedLocales as readonly string[]).includes(locale ?? '') ? (locale as AppLocale) : defaultLocale;
}

export function hasLocaleMessages(locale: string | null | undefined): boolean {
  return Boolean(loaded[toAppLocale(locale)]);
}

/**
 * Loads a locale's catalogue (a no-op when it's already there, e.g. always on the server).
 * Concurrent calls for the same locale share one request.
 */
export function ensureLocaleMessages(locale: string | null | undefined): Promise<Messages> {
  const loc = toAppLocale(locale);
  const ready = loaded[loc];
  if (ready) return Promise.resolve(ready);
  pending[loc] ??= loaders[loc]().then(
    (mod) => {
      loaded[loc] = mod.default;
      return mod.default;
    },
    (err) => {
      // Let the next call retry (e.g. a transient chunk-load failure).
      delete pending[loc];
      throw err;
    },
  );
  return pending[loc]!;
}

/**
 * The locale's catalogue, synchronously. Unknown locales fall back to English, same as before.
 *
 * In the browser, a locale nobody awaited `ensureLocaleMessages()` for isn't loaded yet; that's a
 * bug in the caller's entry point, so it logs once and returns an empty catalogue (react-intl and
 * the `?? defaultMessage` fallbacks then render the English source strings) rather than throwing.
 */
export function getLocaleMessages(locale: string | null | undefined): Messages {
  const loc = toAppLocale(locale);
  const ready = loaded[loc];
  if (ready) return ready;
  if (!warnedMissing.has(loc)) {
    warnedMissing.add(loc);
    console.error(
      `[i18n] "${loc}" messages were read before they were loaded; await ensureLocaleMessages("${loc}") before rendering.`,
    );
  }
  return EMPTY;
}
